import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import Avatar from 'react-avatar'
import { toast } from 'react-toastify'
import { TApi, TRoles } from '../../../../shared/const'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import http from '../../../../shared/http'
import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Table from '../../../../UI/Table'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'
import Pagination from '../../../../UI/Pagination'
import Search from '../../../../UI/Search'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { emailConfirmedField, expertConfirmedField, stateField } from '../../../../UI/FilterPanel/fields'
import { blobTypes, downloadFile, stateFullLabel, stateLabel } from '../../../../shared/helpers'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'
import Icon from '../../../../UI/Icon'
import { connect } from 'react-redux'

function Experts(props) {
	const { t } = useTranslation()
	const [countExperts, setCountExperts] = useState(0)
	const [page, setPage] = useState(0)
	const limit = 10
	const [isLoading, setLoading] = useState(false)
	const [sort, setSort] = useState({ name: 'name', direction: 'desc' })
	const [experts, setExperts] = useState([])
	const [search, setSearch] = useState('')
	const [openFilter, setOpenFilter] = useState(false)

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	// Bundesland-Admins sehen ohnehin nur ihr eigenes Bundesland (das setzt das
	// Backend), ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			expertConfirmedField(t),
			emailConfirmedField(t)
		],
		[t, props.role]
	)

	const filters = useFilters({ storageKey: 'experts', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchExperts = () => {
		const isCurrent = latest()

		http.get(TApi.ADMIN_EXPERTS, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			setExperts(data.rows)
			setCountExperts(data.count)
		})
	}

	const deleteExpert = id => {
		setLoading(true)
		http.delete(TApi.ADMIN_EXPERT_ID(id))
			.then(() => {
				toast.success(t('The expert was deleted successfully'))
				fetchExperts()
			})
			.finally(() => setLoading(false))
	}

	const resendEmailConfirmation = id => {
		setLoading(true)
		http.post(TApi.ADMIN_RESEND_EMAIL_EXPERT(id))
			.then(() => {
				toast.success(t('The request was successfully resend'))
				fetchExperts()
			})
			.finally(() => setLoading(false))
	}
	/*
	const confirmExpert = id => {
		setLoading(true)
		http
			.put(TApi.ADMIN_CONFIRM_EXPERT(id))
			.then(() => {
				toast.success(t('The expert is confirmed'))
				fetchExperts()
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const unConfirmExpert = id => {
		setLoading(true)
		http
			.put(TApi.ADMIN_UNCONFIRM_EXPERT(id))
			.then(() => {
				toast.success(t('The expert was rejected'))
				fetchExperts()
			})
			.finally(() => {
				setLoading(false)
			})
	}
*/
	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchExperts()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, debouncedSearch, filterQuery])

	const columns = useMemo(
		() => [
			{
				Header: () => {
					return <span style={{ paddingLeft: 20 }}>Name</span>
				},
				accessor: originalRow => {
					return {
						avatar: originalRow.avatar,
						firstName: originalRow.firstName,
						lastName: originalRow.lastName
					}
				},
				id: 'name',
				minWidth: 250,
				Cell: ({ row }) => {
					return (
						<div className={styles.name}>
							<div>
								<Avatar
									style={{ marginLeft: 20 }}
									src={row.values.name.avatar}
									round
									size={32}
									name={`${row.values.name.firstName} ${row.values.name.lastName}`}
								/>
								<span
									className={styles.nameText}
								>{`${row.values.name.firstName} ${row.values.name.lastName}`}</span>
							</div>
						</div>
					)
				}
			},
			{
				Header: 'Email',
				accessor: 'email',
				width: 200,
				Cell: ({ row }) => {
					return <div style={{ wordBreak: 'break-word' }}>{row.original.email}</div>
				}
			},
			{
				Header: t('Company'),
				accessor: 'company',
				disableSortBy: true,
				Cell: ({ row }) => {
					return <div>{row.original.Company?.name}</div>
				}
			},
			{
				Header: t('state'),
				accessor: 'state',
				cellToolTip: ({ row }) => stateFullLabel(row.original.Company?.state),
				Cell: ({ row }) => stateLabel(row.original.Company?.state)
			},
			{
				Header: t('phone'),
				accessor: 'phone',
				width: 110,
				Cell: ({ row }) => {
					return <p style={{ fontSize: 13 }}>{row.values.phone}</p>
				}
			},
			/*
			{
				Header: t('Status'),
				accessor: 'expertConfirmed',
				Cell: ({ row }) => {
					return (
						<span>
							{row.values.expertConfirmed ? (
								<div className={styles.accepted}>{t('Verified')}</div>
							) : (
								<div className={styles.pending}>{t('Not Verified')}</div>
							)}
						</span>
					)
				}
			},
			*/
			{
				Header: 'Email Status',
				accessor: 'emailConfirmed',
				Cell: ({ row }) => {
					return (
						<div className={styles.branchContainer}>
							<span>
								{row.values.emailConfirmed ? (
									<div className={styles.accepted}>{t('Accepted')}</div>
								) : (
									<div className={styles.pending}>{t('Pending')}</div>
								)}
							</span>
						</div>
					)
				},
				width: 110
			},
			{
				accessor: 'id',
				width: 80,
				disableSortBy: true,
				Cell: ({ row }) => {
					return (
						<div className={styles.btnContainer}>
							{!row.values.emailConfirmed && (
								<Btn
									tooltip="Resend request"
									loading={isLoading}
									className={styles.resendBtn}
									iconColor="#000"
									iconSize={{ w: 16, h: 16 }}
									icon={isLoading ? '' : 'resend'}
									theme="white"
									onClick={() => {
										resendEmailConfirmation(row.original.id)
									}}
								/>
							)}

							<Btn
								loading={isLoading}
								iconColor={isLoading ? '#000' : 'none'}
								iconSize={isLoading ? { w: 16, h: 16 } : { w: 20, h: 20 }}
								icon={isLoading ? '' : 'edit'}
								theme="white"
								onClick={() => {
									eventEmitter.emit(
										types.openAddExpertModal,
										experts.find(item => item.id === row.original.id),
										fetchExperts
									)
								}}
							/>
							<Btn
								loading={isLoading}
								className={styles.deleteBtn}
								iconColor="#000"
								iconSize={{ w: 16, h: 16 }}
								icon={isLoading ? '' : 'delete'}
								theme="white"
								onClick={() => {
									if (
										window.confirm(
											t('Are you sure you want to remove this expert?', {
												fullName: `${row.values.name.firstName} ${row.values.name.lastName}`
											})
										)
									)
										deleteExpert(row.original.id)
								}}
							/>
						</div>
					)
				}
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[experts, isLoading, t]
	)
	const downoloadList = fileType => {
		setLoading(true)
		http.get(TApi.ADMIN_DOWNOLOAD_EXPERTS_LIST, {
			params: {
				type: fileType,
				leng: localStorage.getItem('i18nextLng')
			},
			responseType: 'arraybuffer'
		})
			.then(res => {
				var blob = new Blob([res.data], {
					type: blobTypes[fileType]
				})
				downloadFile('Experts', blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('Economic experts')} bg="#f8faff">
				<div className={styles.headerContent}>
					<Search
						value={search}
						onChange={e => handleSearchChange(e.target.value)}
						placeholder={t('Find a user')}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter
						icon="arrow"
						onClick={() => setOpenFilter(true)}
						count={filters.count}
					>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('experts')} countAll={countExperts} onChange={setPage} />
				<div className={styles.downloadContent}>
					{t('Download list as')}
					<div>
						{!isLoading && (
							<Btn
								theme="download"
								iconSize={{ w: 24, h: 24 }}
								icon="download"
								onClick={() => {
									downoloadList('XLS')
								}}
							>
								XLS
							</Btn>
						)}
						{!isLoading && (
							<Btn
								theme="download"
								iconSize={{ w: 24, h: 24 }}
								icon="download"
								onClick={() => {
									downoloadList('PDF')
								}}
							>
								PDF
							</Btn>
						)}
						{isLoading && (
							<Icon
								className={clsx(styles.icon, styles.rootLoading)}
								size={{ w: 16, h: 16 }}
								name="loading"
								color={'#22404D'}
							/>
						)}
					</div>
				</div>
			</div>
			<div className={clsx('container', styles.tableContainer)}>
				<Table
					columns={columns}
					handleSort={handleSort}
					items={experts}
					limit={limit}
					countAll={countExperts}
					onChange={setPage}
					page={page}
					onRowClick={expert => eventEmitter.emit(types.openAddExpertModal, expert, fetchExperts)}
					rowTitle={t('openRecord')}
				/>
			</div>
			<FilterPanel
				open={openFilter}
				onClose={() => setOpenFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}
const mapStateToProps = state => ({
	role: state.user.role
})

export default connect(mapStateToProps)(Experts)
